import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, computed, get } from "@ember/object"
import { inject as service } from "@ember/service"
import type { CardStack, CardStackSummary } from "@shared/models/card-stack"
import type { Course, Unit } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import belongsTo from "cc-frontend/helpers/belongs-to"
import findMany from "cc-frontend/helpers/find-many"
import { payloadForComponent } from "cc-frontend/lib/action-creators/app-state"
import type { PromiseObject } from "cc-frontend/lib/promise-object"
import type AppState from "cc-frontend/services/app-state"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type Session from "cc-frontend/services/session"
import { extractCardStackTemplateIds } from "cc-frontend/utils/filter-dates/course"
import { toDown, toLeft, toRight, toUp } from "ember-animated/transitions/move-over"
import _ from "lodash"
import { compact, concat, flatMap, map, uniq } from "lodash-es"

let headerNameStack: Array<string> = []
let bodyNameStack: Array<string> = []
@tagName("")
export default class UnitMenu extends Component {
  // args
  modelsNeededForFirstEdit!: ModelsNeededForFirstEdit
  planbook!: Planbook
  rotationCalendar!: RotationCalendar
  course!: Course
  unitInfo!: Unit
  courseTitle!: string
  hasNoTemplate!: boolean
  editCourseTitleAndOrder!: Function
  unitCardStackIsFulfilled!: boolean
  unitCardStack!: CardStack
  closeMenu!: Function

  // services
  @service("ember-store") store!: EmberStore
  @service dispatcher!: Dispatcher
  @service session!: Session
  @service lessonEditor!: LessonEditor
  @service modalManager!: ModalManager
  @service dialog!: Dialog
  @service appState!: AppState
  @service planbookEditor!: PlanbookEditor

  menuName = "PRIMARY"
  headerNameStack = ["planner/menus/unit-menu/primary/title"]
  bodyNameStack = ["planner/menus/unit-menu/primary/body"]

  headerAnimationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal === undefined || oldVal === null) return toUp
    if (newVal === undefined || newVal === null) return toUp
    let newValInner = Object.getOwnPropertySymbols(newVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )!
    let oldValInner = Object.getOwnPropertySymbols(oldVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )!
    let newComponentName = newVal[newValInner].name
    let oldComponentName = oldVal[oldValInner].name

    // we're going backwards
    if (
      _.includes(headerNameStack, newComponentName) &&
      _.includes(headerNameStack, oldComponentName)
    ) {
      _.pull(headerNameStack, oldComponentName)
      return toDown
    } else {
      headerNameStack.push(newComponentName)
      return toUp
    }
  }

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal === undefined || oldVal === null) return toLeft
    if (newVal === undefined || newVal === null) return toLeft
    let newValInner = Object.getOwnPropertySymbols(newVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )!
    let oldValInner = Object.getOwnPropertySymbols(oldVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )!
    let newComponentName = newVal[newValInner].name
    let oldComponentName = oldVal[oldValInner].name

    // For the first check, there's an odd case where the new is null and then the new is present and the old is null
    // and this just makes sure we're always going the right way if we're returning home.
    if (
      newComponentName === "planner/menus/unit-menu/primary/body" ||
      (_.includes(bodyNameStack, newComponentName) && _.includes(bodyNameStack, oldComponentName))
    ) {
      // we're going backwards
      _.pull(bodyNameStack, oldComponentName)
      return toRight
    } else {
      bodyNameStack.push(newComponentName)
      bodyNameStack = _.uniq(bodyNameStack)
      return toLeft
    }
  }

  @findMany("templateIds", "card-stack-summary") usedTemplates!: PromiseObject<
    Array<CardStackSummary>
  >

  @belongsTo("planbook", "courses") courses

  @computed("planbook", "courses")
  get templateIds() {
    let planbookTemplateIds = map(
      get(this.planbook.relationships.cardStackTemplates, "data"),
      (datum) => get(datum, "id"),
    )
    let courseTemplateIds = uniq(
      compact(flatMap(this.courses.content, (course) => extractCardStackTemplateIds(course))),
    )
    return compact(uniq(concat(planbookTemplateIds, courseTemplateIds)))
  }

  @action
  editCourse() {
    this.editCourseTitleAndOrder(this.course.id)
    this.closeMenu()
  }

  @action
  goToReplaceTemplate() {
    this.set("menuName", "REPLACE_TEMPLATE")
  }

  @action
  goToTemplateGallery() {
    this.modalManager.openModal("planner/modals/template-library", {
      planbook: this.planbook,
      rotationCalendar: this.rotationCalendar,
      course: this.course,
      cardStack: this.unitCardStack,
      modelsNeededForFirstEdit: this.modelsNeededForFirstEdit,
    })
  }

  @action
  assignTemplateToUnit(templateId) {
    this.store.find("card-stack", templateId).then((cardStack: CardStack) => {
      this.lessonEditor.pasteCardStack(
        this.modelsNeededForFirstEdit,
        this.unitCardStack.id,
        this.course,
        this.planbook,
        cardStack.attributes,
        this.unitCardStack.attributes,
        "template",
        {
          id: templateId,
          version: cardStack.attributes.version,
          planbookId: cardStack.relationships.planbook.data.id,
          courseId: null,
          // I think this is right -- we should really get the ownerIds from the planbook that's referenced in the card stack
          ownerIds: [this.planbook.relationships.owner.data.id],
          source: "template",
        },
      )
      this.closeMenu()
    })
  }

  @action
  backToPrimaryMenu() {
    this.set("menuName", "PRIMARY")
  }

  @action
  saveUnitAsTemplate() {
    window
      .swal({
        text: "What would you like to call this template?",
        type: "question",
        input: "text",
        inputPlaceholder: "Template name",
        showCancelButton: true,
      })
      .then((val) => {
        this.planbookEditor.saveAsLessonTemplate(
          this.course,
          this.planbook,
          this.unitCardStack.attributes,
          val,
        )

        this.closeMenu()
      })
      .then(() => {
        this.closeMenu()

        this.dispatcher.dispatch(
          "CLOSE_UNIT_COMPARISON_MODAL",
          payloadForComponent(this.get("appState"), "unit-comparison-modal"),
        )
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}
